import React, { useRef, useEffect } from "react";
import { Popper } from "@mui/material";
import "./FeedbackPopover.scss";
import { PulseButton, PulseButtonTheme } from "@pulselabs-ai/shared-fe-ui";
import { ChangeIcon, SurveyCommentIcon, SurveyRadiogroupIcon } from "../icons";

// Define types for survey schema
interface SurveyChoice {
  text: string;
  value: string;
}

interface SurveyElement {
  name: string;
  type: string;
  title: string;
  choices?: SurveyChoice[];
  isRequired?: boolean;
  colCount?: number;
  otherText?: string;
  showOtherItem?: boolean;
}

interface SurveyPage {
  name: string;
  elements: SurveyElement[];
}

interface SurveySchema {
  pages: SurveyPage[];
  title: string;
  description: string;
}

// Define type for form values based on survey elements
type FormValue = string | number | boolean | null;
type FormValues = Record<string, FormValue>;

interface FeedbackPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onChangeFeedbackForm: () => void;
  surveySchema: SurveySchema;
}

const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  onChangeFeedbackForm,
  surveySchema,
}) => {
  // Create state to hold form values - replacing 'any' with specific types
  const feedbackId = open ? "feedback-popper" : undefined;
  const popoverRef = useRef<HTMLDivElement>(null);

  // Reset form values when schema changes
  useEffect(() => {
    const initialValues: FormValues = {};

    surveySchema.pages.forEach((page) => {
      page.elements.forEach((element) => {
        // Initialize with appropriate default based on element type
        if (element.type === "radiogroup") {
          initialValues[element.name] = "";
        } else if (element.type === "comment") {
          initialValues[element.name] = "";
        } else {
          initialValues[element.name] = null;
        }
      });
    });
  }, [surveySchema]);

  // Add event listener to handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, anchorEl]);

  const handleChangeFeedbackForm = () => {
    onClose();
    onChangeFeedbackForm();
  };

  // Function to render form elements based on their type
  const renderFormElement = (element: SurveyElement) => {
    switch (element.type) {
      case "radiogroup":
        return (
          <div key={element.name} className="form-item">
            <div className="form-control">
              <div className="body-s --color-text-secondary">
                <span className="icon-container">
                  <SurveyRadiogroupIcon />
                </span>
                {element.title}
              </div>
            </div>
          </div>
        );

      case "comment":
        return (
          <div key={element.name} className="form-item">
            <div className="form-control">
              <div className="body-s --color-text-secondary">
                <span className="icon-container">
                  <SurveyCommentIcon />
                </span>
                {element.title}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Popper
      id={feedbackId}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [20, 36],
          },
        },
      ]}
    >
      <div className="feedback-form-panel" ref={popoverRef}>
        <div className="sub-headline-xs">{surveySchema.title}</div>
        <div className="body-s --color-text-secondary">
          {surveySchema.description}
        </div>

        <div className="survey-elements">
          {surveySchema.pages.map((page) =>
            page.elements.map((element) => renderFormElement(element)),
          )}
        </div>

        <PulseButton
          fullWidth
          onClick={handleChangeFeedbackForm}
          className="change-form-button"
          theme={PulseButtonTheme.TERTIARY}
          startIcon={<ChangeIcon />}
        >
          Change Feedback Form
        </PulseButton>
      </div>
    </Popper>
  );
};

export default FeedbackPopover;
